/**
 * TCOComparison - Side-by-side ICE vs EV Total Cost of Ownership
 *
 * ICE inputs are user-configurable. EV costs auto-populated from simulation results.
 */

import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Paper,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    alpha,
} from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

const FUEL_TYPES = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'gasoline', label: 'Gasoline' },
];

function formatCurrency(value) {
    if (value === null || value === undefined) return '-';
    return '$' + Math.round(value).toLocaleString();
}

function CostRow({ label, iceValue, evValue, highlight }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.75,
                px: 1,
                bgcolor: highlight ? alpha(vortxColors.teal, 0.05) : 'transparent',
                borderRadius: 0.5,
            }}
        >
            <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
            <Typography variant="body2" sx={{ width: 120, textAlign: 'right' }}>
                {formatCurrency(iceValue)}
            </Typography>
            <Typography variant="body2" sx={{ width: 120, textAlign: 'right' }}>
                {formatCurrency(evValue)}
            </Typography>
        </Box>
    );
}

function TCOComparison({ iceBaseline, onIceChange, tcoResults, evAnnualCost }) {
    const handleChange = (field, value) => {
        onIceChange({ ...iceBaseline, [field]: value });
    };

    const handleNumberChange = (field) => (e) => {
        const val = e.target.value === '' ? '' : parseFloat(e.target.value);
        if (e.target.value !== '' && isNaN(val)) return;
        handleChange(field, val);
    };

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Total Cost of Ownership — ICE vs EV
            </Typography>

            {/* ICE Baseline Inputs */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                    ICE Fleet Baseline
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Fuel Type</InputLabel>
                            <Select
                                value={iceBaseline?.fuel_type || 'diesel'}
                                label="Fuel Type"
                                onChange={(e) => handleChange('fuel_type', e.target.value)}
                            >
                                {FUEL_TYPES.map(ft => (
                                    <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Fuel Price"
                            type="number"
                            value={iceBaseline?.price_per_gallon ?? 4.50}
                            onChange={handleNumberChange('price_per_gallon')}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: <InputAdornment position="end">/gal</InputAdornment>,
                            }}
                            inputProps={{ min: 0, step: 0.10 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Fleet MPG"
                            type="number"
                            value={iceBaseline?.fleet_mpg ?? 6.0}
                            onChange={handleNumberChange('fleet_mpg')}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">mpg</InputAdornment>,
                            }}
                            inputProps={{ min: 0.1, step: 0.5 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Maintenance"
                            type="number"
                            value={iceBaseline?.maintenance_per_mile ?? 0.22}
                            onChange={handleNumberChange('maintenance_per_mile')}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: <InputAdornment position="end">/mi</InputAdornment>,
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Annual Miles/Vehicle"
                            type="number"
                            value={iceBaseline?.annual_miles_per_vehicle ?? 50000}
                            onChange={handleNumberChange('annual_miles_per_vehicle')}
                            inputProps={{ min: 0, step: 1000 }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Comparison Table */}
            {tcoResults ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                            Annual Operating Costs
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ width: 120, textAlign: 'right' }}>
                            ICE
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ width: 120, textAlign: 'right' }}>
                            EV
                        </Typography>
                    </Box>

                    <CostRow
                        label="Fuel / Electricity"
                        iceValue={tcoResults.ice_year1?.fuel}
                        evValue={tcoResults.ev_year1?.electricity}
                    />
                    <CostRow
                        label="Maintenance"
                        iceValue={tcoResults.ice_year1?.maintenance}
                        evValue={tcoResults.ev_year1?.maintenance}
                    />
                    <CostRow
                        label="Total Annual"
                        iceValue={tcoResults.ice_year1?.total}
                        evValue={tcoResults.ev_year1?.total}
                        highlight
                    />

                    {/* Savings summary */}
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: tcoResults.year1_savings > 0
                                ? alpha(vortxColors.teal, 0.1)
                                : alpha('#f44336', 0.1),
                            border: `1px solid ${tcoResults.year1_savings > 0
                                ? alpha(vortxColors.teal, 0.3)
                                : alpha('#f44336', 0.3)}`,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Year 1 Savings
                                </Typography>
                                <Typography variant="h6" fontWeight={600}
                                    sx={{ color: tcoResults.year1_savings > 0 ? vortxColors.teal : '#f44336' }}>
                                    {formatCurrency(tcoResults.year1_savings)}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Lifetime Savings
                                </Typography>
                                <Typography variant="h6" fontWeight={600}
                                    sx={{ color: tcoResults.lifetime_savings > 0 ? vortxColors.teal : '#f44336' }}>
                                    {formatCurrency(tcoResults.lifetime_savings)}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    CapEx Investment
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {formatCurrency(tcoResults.ev_year1?.capex)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            ) : (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {evAnnualCost
                            ? 'Enter ICE baseline and run financial analysis to see TCO comparison.'
                            : 'Run simulation first to generate EV operating costs.'}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}

export default TCOComparison;
